import { PLAY_TIMING, LIVE_FRAME_MS } from '../constants.js';
import { snapToPixel, clampRange, clamp01 } from '../util/math.js';
import { pathDistance } from '../util/interpolation.js';
import { stopReasonForPlay, visualEndXForPlay, isScrimmageSnap, isSpecialTeamsPlay, playHash, visualYardsForPlay, isInterceptionPlay } from './helpers.js';
import { goalLineXForDirection, screenXForYard } from '../field/metrics.js';
import { buildFormation } from './formation.js';
import { addLiveTempo, playerPixelsPerFrame, runPixelsPerSecondForSpeed } from './speed.js';
import { findPlayer } from '../player/model.js';
import { routePlan, buildRouteShapePoints, buildPostCatchPoints, buildInterceptionReturnPoints, interceptionReturnEndPoint, turnoverDefenderRole } from './waypoints.js';
import { buildPresentationPlan, applyPresentationPlan } from './presentation-plan.js';
import { canonicalTeamId } from '#shared';
import { fumblePresentedFrameCount } from './turnover-cadence.js';

const ARCADE_SIM_FRAME_MS = 1000 / 60;
function rawInterceptionPresentedFrameCount(play: any) {
  if (!isInterceptionPlay(play) || !Array.isArray(play?.arcadeReplay?.frames)) {
    return null;
  }
  // Interception returns use the authoritative frame cadence. Touchdown
  // presentation appends a short finish and hold without resampling the run.
  const touchdown = play?.defensiveScore || stopReasonForPlay(play) === 'touchdown';
  return play.arcadeReplay.frames.length + (touchdown ? 30 : 0);
}

// Parameterized: takes game object instead of reading state.game
export function directionForOffense(offense: any, game: any): number {
  return game && offense.id === game.home.id ? -1 : 1;
}

function durationMsForTravel(distancePx: number, pixelsPerSecond: number, extraMs: number = 0): number {
  return Math.round(Math.max(0, distancePx) / Math.max(1, pixelsPerSecond) * 1000 + extraMs);
}

export function liveFrameCount(play: any): number {
  return Math.max(14, Math.round(livePlayDurationMs(play) / LIVE_FRAME_MS));
}

export function kickoffLiveDurationMs(play: any): number {
  const rawArcadeKickoff = play.arcadeReplay?.source === 'arcade-sim-v1'
    && play.arcadePlaybackMode === 'raw';
  if (rawArcadeKickoff && Array.isArray(play.arcadeReplay?.frames) && play.arcadeReplay.frames.length > 0) {
    return Math.max(2400, Math.round(play.arcadeReplay.frames.length * ARCADE_SIM_FRAME_MS));
  }
  const stopReason = stopReasonForPlay(play);
  const hangMs = Math.round((play.hangTime || 4.1) * 1000);
  const returnYards = Math.max(0, play.returnYards || 0);
  if (stopReason === 'touchback') return Math.max(4700, hangMs + 900);
  if (stopReason === 'dead-short') return Math.max(4400, hangMs + 700);
  return Math.max(7600, 1700 + hangMs + returnYards * 102);
}

export function kickoffFlightWindow(play: any): { start: number; end: number } {
  const liveMs = Math.max(1, kickoffLiveDurationMs(play));
  return {
    start: 0,
    end: clamp01(Math.round((play.hangTime || 4.1) * 1000) / liveMs),
  };
}

export function puntHangTimeSeconds(play: any): number {
  const explicitHang = Number(play?.hangTime);
  if (Number.isFinite(explicitHang) && explicitHang > 0) {
    return clampRange(explicitHang, 3.85, 5.15);
  }
  const grossYards = clampRange(Math.round(play?.grossYards || 42), 28, 58);
  const distanceBias = grossYards - 42;
  return clampRange(
    4.34
      - Math.max(0, distanceBias) * 0.019
      + Math.max(0, -distanceBias) * 0.012,
    3.85,
    5.15
  );
}

export function puntOperationSeconds(play: any): number {
  const explicitOperation = Number(play?.operationTime);
  if (Number.isFinite(explicitOperation) && explicitOperation > 0) {
    return clampRange(explicitOperation, 1.84, 2.1);
  }

  // NFL.com cites 2.1 seconds as the standard maximum snap-to-kick time on punts.
  // We keep most reps just under that benchmark with small deterministic variation.
  const snapAdjustment = (playHash(play, 'punt-operation-time') % 7) * 0.03;
  return clampRange(1.92 + snapAdjustment, 1.84, 2.1);
}

export function puntVisualHangSeconds(play: any): number {
  const stopReason = stopReasonForPlay(play);
  const returnYards = Math.max(0, play.returnYards || 0);
  const tackledReturn = stopReason === 'tackle';
  const shortReturnFinish = tackledReturn && returnYards <= 10;
  const mediumReturnFinish = tackledReturn && returnYards > 10 && returnYards <= 20;
  const visualHangBonus = 0.18
    + clampRange((12 - returnYards) * 0.008, 0, 0.1)
    + (shortReturnFinish ? 0.08 : mediumReturnFinish ? 0.03 : 0);

  return puntHangTimeSeconds(play) + visualHangBonus;
}

export function puntLiveDurationMs(play: any): number {
  const rawArcadePunt = play.arcadeReplay?.source === 'arcade-sim-v1'
    && play.arcadePlaybackMode === 'raw';
  if (rawArcadePunt && Array.isArray(play.arcadeReplay?.frames) && play.arcadeReplay.frames.length > 0) {
    return Math.max(2600, Math.round(play.arcadeReplay.frames.length * ARCADE_SIM_FRAME_MS));
  }
  const stopReason = stopReasonForPlay(play);
  const operationMs = Math.round(puntOperationSeconds(play) * 1000);
  const hangMs = Math.round(puntHangTimeSeconds(play) * 1000);
  const returnYards = Math.max(0, play.returnYards || 0);
  const grossYards = Math.max(0, play.grossYards || 0);

  if (stopReason === 'touchback') {
    return Math.max(6000, operationMs + hangMs + 520 + Math.max(0, grossYards - 36) * 10);
  }

  if (stopReason === 'dead-short') {
    return Math.max(5600, operationMs + hangMs + 460 + Math.max(0, grossYards - 34) * 6);
  }

  if (stopReason === 'touchdown') {
    // Punt return TDs have no finish-loop — the entire return must fit in the tracked-frame budget.
    // 120 ms/yard gives the returner ~3.6 frames/yard, which animates at ~10 yards/s (realistic).
    return Math.max(10000, operationMs + hangMs + 620 + returnYards * 120 + 400);
  }

  return Math.max(
    7000,
    operationMs
      + hangMs
      + 620
      + Math.max(0, grossYards - 36) * 8
      + returnYards * 54
      + (stopReason === 'out-of-bounds' ? 140 : 260)
  );
}

export function puntFlightWindow(play: any): { start: number; end: number } {
  const liveMs = Math.max(1, puntLiveDurationMs(play));
  const startMs = Math.round(puntOperationSeconds(play) * 1000);
  const endMs = startMs + Math.round(puntVisualHangSeconds(play) * 1000);

  return {
    start: clamp01(startMs / liveMs),
    end: clamp01(endMs / liveMs),
  };
}

// Simplified version that doesn't need game context for most play types.
// For run/pass plays that need path profiles, uses approximate durations.
export function livePlayDurationMs(play: any): number {
  const stopReason = stopReasonForPlay(play);
  const resultText = (play.result || '').toLowerCase();
  const netYards = Math.max(0, Math.abs((play.endYardLine ?? play.startYardLine ?? 0) - (play.startYardLine ?? 0)));
  const airYards = Math.max(0, play.airYards || 0);
  const yacYards = Math.max(0, play.yacYards || 0);
  const returnYards = Math.max(0, play.returnYards || 0);
  const grossYards = Math.max(0, play.grossYards || 0);

  if (play.playId === 'kneel') return 1350;
  if ((play.playId === 'field-goal' || play.playId === 'pat')
    && play.arcadeReplay?.source === 'arcade-sim-v1'
    && play.arcadePlaybackMode === 'raw'
    && Array.isArray(play.arcadeReplay?.frames)
    && play.arcadeReplay.frames.length > 0) {
    return Math.max(2200, Math.round(play.arcadeReplay.frames.length * ARCADE_SIM_FRAME_MS));
  }
  if (play.playId === 'field-goal' || play.playId === 'pat') return 2100;
  if (play.playId === 'kickoff') return kickoffLiveDurationMs(play);

  if (play.playId === 'punt') {
    return puntLiveDurationMs(play);
  }

  if (play.family === 'run') {
    const rawArcadeRun = play.arcadeReplay?.source === 'arcade-sim-v1'
      && play.arcadePlaybackMode === 'raw';
    if (rawArcadeRun && Array.isArray(play.arcadeReplay?.frames) && play.arcadeReplay.frames.length > 0) {
      return Math.max(2300, Math.round(fumblePresentedFrameCount(play.arcadeReplay) * ARCADE_SIM_FRAME_MS));
    }
    // Approximate motion duration using netYards as proxy for path distance
    const approxDistance = 176 + netYards * 22 * 0.72;
    const carrierSpeed = 84;
    const motionMs = durationMsForTravel(approxDistance, runPixelsPerSecondForSpeed(carrierSpeed));
    const settleMs = play.playId === 'kneel' ? 220 : 420;
    const fumbleTurnover = !!play.turnover && /fumble/i.test(play.result || '');
    const turnoverReturnYards = Math.max(0, play.returnYards || 0);
    const finishBonus = stopReason === 'touchdown' ? 160
      : stopReason === 'out-of-bounds' ? 90
      : stopReason === 'tackle' && netYards >= 10 ? 420 + Math.min(620, netYards * 20)
      : 0;
    const turnoverBonus = fumbleTurnover
      ? 720 + turnoverReturnYards * (stopReason === 'touchdown' ? 122 : 102) + (turnoverReturnYards === 0 ? 280 : 0)
      : 0;
    const floorMs = play.playId === 'kneel'
      ? 1100
      : fumbleTurnover
        ? (turnoverReturnYards > 0 ? 3600 : 3200)
        : 2300;
    return Math.max(floorMs, Math.round(motionMs + settleMs + finishBonus + turnoverBonus));
  }

  if (play.family === 'pass') {
    const rawArcadePass = play.arcadeReplay?.source === 'arcade-sim-v1'
      && play.arcadePlaybackMode === 'raw';
    if (rawArcadePass && Array.isArray(play.arcadeReplay?.frames) && play.arcadeReplay.frames.length > 0) {
      const interceptionFrames = rawInterceptionPresentedFrameCount(play);
      if (interceptionFrames !== null) {
        return Math.max(2400, Math.round(interceptionFrames * ARCADE_SIM_FRAME_MS));
      }
      return Math.max(2400, Math.round(fumblePresentedFrameCount(play.arcadeReplay) * ARCADE_SIM_FRAME_MS));
    }
    if (play.scramble) {
      const scrambleYards = Math.max(0, visualYardsForPlay(play));
      return Math.max(
        3000,
        2400
          + scrambleYards * 96
          + (stopReason === 'touchdown' ? 320 : stopReason === 'out-of-bounds' ? 180 : 340)
      );
    }
    if (resultText.includes('sack')) return Math.max(2050, 2050 + netYards * 26);
    if (stopReason === 'incomplete') return Math.max(2450, 2450 + airYards * 34 + (play.playId === 'hail-mary' ? 360 : 0));
    const carrierYards = play.turnover ? returnYards : yacYards;
    const direction = 1;
    const centerY = 305;
    const losX = screenXForYard(play.startYardLine ?? 50, direction);
    const endX = screenXForYard(play.endYardLine ?? play.startYardLine ?? 50, direction);
    const laneY = snapToPixel(centerY + clampRange((play.visual?.lane ?? 0) * 18, -176, 176));
    const formation = buildFormation(play, losX, centerY, direction);
    const plan = routePlan(play, losX, centerY, endX, direction);
    const targetRole = plan.targetRole || 'WR1';
    const actualCatchYard = play.catchYardLine || play.passTargetYardLine || play.endYardLine || play.startYardLine;
    const routeTargetYard = play.turnover
      ? actualCatchYard
      : stopReason === 'incomplete'
        ? (play.passTargetYardLine || actualCatchYard)
        : actualCatchYard;
    const catchX = screenXForYard(actualCatchYard, direction);
    const routeTargetX = screenXForYard(routeTargetYard, direction);
    const routeTargets = Object.fromEntries(
      Object.entries(plan.routes).map(([role, [targetX, targetY]]: [string, any]) => [
        role,
        role === targetRole ? [routeTargetX, targetY] : [targetX, targetY],
      ])
    );
    const catchTarget = routeTargets[targetRole] || [catchX, laneY];
    const catchPoint = { x: catchTarget[0], y: catchTarget[1] };
    const targetBase = (findPlayer(formation.offense, targetRole) || findPlayer(formation.offense, 'WR1') || formation.offense[0])!;
    const targetRoutePoints = buildRouteShapePoints(play, targetRole, { x: targetBase.x, y: targetBase.y }, catchPoint, losX, centerY, direction);
    const pickPoint = play.turnover
      ? { x: snapToPixel(catchPoint.x + 10), y: snapToPixel(catchPoint.y) }
      : catchPoint;
    const returnEndPoint = play.turnover
      ? interceptionReturnEndPoint(play, pickPoint, endX, laneY, centerY)
      : { x: endX, y: laneY };
    const postCatchPoints = play.turnover
      ? buildInterceptionReturnPoints(play, pickPoint, returnEndPoint, direction, centerY)
      : buildPostCatchPoints(play, catchPoint, returnEndPoint);
    const scoreBonus = stopReason === 'touchdown' ? 180 : 0;
    const deepTackleLike = stopReason === 'tackle'
      && (airYards >= 14 || (airYards >= 12 && netYards >= 18) || (airYards >= 10 && yacYards >= 6));
    const deepTackleBonus = deepTackleLike ? 760 + carrierYards * 18 : 0;
    const airFactor = clamp01(Math.max(0, airYards - 5) / 24);
    const dropFrames = clampRange(Math.round(8 + airFactor * 4), 8, 14);
    const flightFrames = clampRange(Math.round(Math.max(6, airYards / 2.1)), 6, Math.max(7, dropFrames + 14));
    const routeFrames = Math.max(
      dropFrames + flightFrames + 2,
      Math.round(pathDistance(targetRoutePoints) / playerPixelsPerFrame(targetBase.speedRating || 78, 'pass'))
    );
    const turnoverRole = play.turnover ? turnoverDefenderRole(targetRole) : null;
    const turnoverCarrier = turnoverRole ? findPlayer(formation.defense, turnoverRole) : null;
    const postCatchCarrier = play.turnover ? turnoverCarrier : targetBase;
    const yacFrames = Math.max(
      carrierYards > 0 ? 6 : 3,
      Math.round(
        pathDistance(postCatchPoints) / playerPixelsPerFrame(
          postCatchCarrier?.speedRating || 78,
          'pass',
          play.turnover || play.playId === 'screen-left' ? 0.94 : 0.9
        )
      )
    );
    const tackleTailFrames = stopReason === 'tackle'
      ? deepTackleLike ? 13 : (carrierYards >= 8 || netYards >= 18 ? 10 : 8)
      : stopReason === 'touchdown' || stopReason === 'out-of-bounds' ? 4 : 0;
    const base = 2600 + airYards * 34 + carrierYards * (play.turnover ? 42 : 28);
    const turnoverBonus = play.turnover ? 420 + returnYards * 18 : 0;
    const motionFloor = (routeFrames + yacFrames + tackleTailFrames) * LIVE_FRAME_MS;
    return Math.max(2550, Math.round(Math.max(base + turnoverBonus + scoreBonus + deepTackleBonus, motionFloor)));
  }

  return Math.max(2200, PLAY_TIMING.livePlayMs);
}

export function scrimmageChangeoverMs(play: any, previousPlay: any = null): number {
  if (!isScrimmageSnap(play) || !previousPlay) {
    return 0;
  }
  if (isSpecialTeamsPlay(previousPlay)) {
    return PLAY_TIMING.specialTeamsExitMs;
  }
  if (
    isScrimmageSnap(previousPlay)
    && (previousPlay.offense !== play.offense || previousPlay.defense !== play.defense)
  ) {
    return PLAY_TIMING.scrimmageChangeoverMs;
  }
  return 0;
}

export function playDurationMs(play: any, playIndex: number, previousPlay: any = null): number {
  const deadHoldMs = playIndex > 0 ? PLAY_TIMING.deadHoldMs : 0;
  const liveMs = livePlayDurationMs(play);
  if (isScrimmageSnap(play)) {
    const changeoverMs = scrimmageChangeoverMs(play, previousPlay);
    return deadHoldMs + changeoverMs + PLAY_TIMING.huddleRunMs + PLAY_TIMING.breakHuddleMs + PLAY_TIMING.preSnapHoldMs + liveMs;
  }
  if (isSpecialTeamsPlay(play)) {
    const exitMs = playIndex > 0 ? PLAY_TIMING.specialTeamsExitMs : 0;
    return deadHoldMs + exitMs + PLAY_TIMING.specialTeamsEnterMs + PLAY_TIMING.specialTeamsSetMs + liveMs;
  }
  return deadHoldMs + liveMs;
}

export interface PlayContext {
  play: any;
  offense: any;
  defense: any;
  direction: number;
  centerY: number;
  losX: number;
  endX: number;
  goalLineX: number;
  firstDownX: number;
  laneY: number;
  formation: { offense: any[]; defense: any[] };
  offensePalette: any;
  defensePalette: any;
  [key: string]: any;
}

// Parameterized to take game instead of reading state.game.
// The teamUniformPalette function must be passed in since it depends on visualConfig (closure-scoped).
export function buildPlayContext(
  play: any,
  centerY: number,
  game: any,
  resolveLaneY: (play: any, centerY: number) => number,
  teamUniformPalette: (team: any, side: string, role: string) => any,
): PlayContext {
  const offenseId = canonicalTeamId(play.offense);
  const offense = canonicalTeamId(game.home.id) === offenseId ? game.home : game.away;
  const defense = canonicalTeamId(game.home.id) === canonicalTeamId(play.defense) ? game.home : game.away;
  const direction = directionForOffense(offense, game);
  const losX = screenXForYard(play.startYardLine, direction);
  const endX = visualEndXForPlay(play, direction);
  const firstDownX = screenXForYard(Math.min(100, play.startYardLine + Math.max(1, play.distance || 0)), direction);
  const laneY = resolveLaneY(play, centerY);
  const formation = addLiveTempo(buildFormation(play, losX, centerY, direction), offense, defense);
  const presentationPlan = buildPresentationPlan(play, {
    centerY,
    laneY,
    losX,
    endX,
    direction,
  });
  const presentationPlay = applyPresentationPlan(play, presentationPlan);

  return {
    play,
    presentationPlan,
    presentationPlay,
    offense,
    defense,
    direction,
    centerY,
    losX,
    endX,
    goalLineX: goalLineXForDirection(direction),
    firstDownX,
    laneY,
    formation,
    offensePalette: teamUniformPalette(offense, 'home', 'offense'),
    defensePalette: teamUniformPalette(defense, 'away', 'defense'),
  };
}
